import React, { useEffect, useRef, useState } from 'react';
import ManagementModalLayout from '../ManagementModalLayout/ManagementModalLayout';
import ConfirmActionModal from '../ConfirmActionModal/ConfirmActionModal';
import './ManageProductCategoriesModal.css';
import {
  createProductCategory,
  deleteProductCategory,
  fetchProductCategories,
  type ManagedProductCategory,
  updateProductCategory,
} from '../../auth/productCategoryManagement';
import { useI18n } from '../../i18n/I18nContext';

interface ManageProductCategoriesModalProps {
  onClose: () => void;
  onCatalogUpdated: () => Promise<void>;
  showToast: (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;
}

interface ProductCategoryDraft {
  id?: number;
  name: string;
  internalId: string;
  isActive: boolean;
}

const emptyDraft = (): ProductCategoryDraft => ({
  name: '',
  internalId: '',
  isActive: true,
});

const ManageProductCategoriesModal: React.FC<ManageProductCategoriesModalProps> = ({
  onClose,
  onCatalogUpdated,
  showToast,
}) => {
  const { t } = useI18n();
  const categoryNameInputId = 'manage-product-category-name';
  const categoryInternalIdInputId = 'manage-product-category-internal-id';
  const categoryNameInputRef = useRef<HTMLInputElement | null>(null);
  const [categories, setCategories] = useState<ManagedProductCategory[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [draft, setDraft] = useState<ProductCategoryDraft>(emptyDraft());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function loadCategories() {
      setLoading(true);
      setError(null);
      try {
        const nextCategories = await fetchProductCategories();
        if (cancelled) {
          return;
        }
        setCategories(nextCategories);
        setSelectedCategoryId(null);
        setDraft(emptyDraft());
      } catch (nextError) {
        if (!cancelled) {
          setError(nextError instanceof Error ? nextError.message : t('productCategories.loadError'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void loadCategories();

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (selectedCategoryId !== null) {
      return;
    }

    const timer = window.setTimeout(() => {
      categoryNameInputRef.current?.focus();
      categoryNameInputRef.current?.select();
    }, 0);

    return () => window.clearTimeout(timer);
  }, [selectedCategoryId]);

  function selectCategory(category: ManagedProductCategory) {
    setSelectedCategoryId(category.id);
    setDraft({
      id: category.id,
      name: category.name,
      internalId: category.internalId ?? '',
      isActive: category.isActive,
    });
    setError(null);
  }

  function startCreateCategory() {
    setSelectedCategoryId(null);
    setDraft(emptyDraft());
    setError(null);
  }

  const isEditingExistingCategory = draft.id != null;

  async function reloadCategories(preferredId?: number | null, resetToCreate = false) {
    const nextCategories = await fetchProductCategories();
    setCategories(nextCategories);

    if (resetToCreate) {
      setSelectedCategoryId(null);
      setDraft(emptyDraft());
    } else {
      const nextSelected =
        (preferredId != null ? nextCategories.find((category) => category.id === preferredId) : undefined) ??
        nextCategories[0];

      if (nextSelected) {
        selectCategory(nextSelected);
      } else {
        setSelectedCategoryId(null);
        setDraft(emptyDraft());
      }
    }

    await onCatalogUpdated();
  }

  async function handleSave() {
    setSaving(true);
    setError(null);

    try {
      if (!draft.name.trim()) {
        throw new Error(t('productCategories.requireName'));
      }
      if (!draft.internalId.trim()) {
        throw new Error(t('productCategories.requireInternalId'));
      }
      if (!/^[a-z0-9]+(?:_[a-z0-9]+)*$/.test(draft.internalId.trim().toLowerCase())) {
        throw new Error(t('productCategories.invalidInternalId'));
      }

      const savedCategory = draft.id
        ? await updateProductCategory({
            id: draft.id,
            name: draft.name,
            internalId: draft.internalId,
            isActive: draft.isActive,
          })
        : await createProductCategory({
            name: draft.name,
            internalId: draft.internalId,
            isActive: draft.isActive,
          });

      if (draft.id != null) {
        await reloadCategories(savedCategory.id);
      } else {
        await reloadCategories(null, true);
      }
      showToast(draft.id ? t('productCategories.updated') : t('productCategories.created'), 'success');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('productCategories.saveError'));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!draft.id) {
      startCreateCategory();
      return;
    }

    setDeleting(true);
    setError(null);

    try {
      await deleteProductCategory(draft.id);
      setConfirmingDelete(false);
      await reloadCategories(selectedCategoryId === draft.id ? null : selectedCategoryId);
      showToast(t('productCategories.deleted'), 'success');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : t('productCategories.deleteError'));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <ManagementModalLayout
        ariaLabel={t('productCategories.title')}
        title={t('productCategories.title')}
        closeAriaLabel={t('productCategories.close')}
        onClose={onClose}
        className="manage-product-categories-modal"
        addButtonLabel={t('productCategories.addCategory')}
        addButtonAriaLabel={t('productCategories.addCategoryAria')}
        onAdd={startCreateCategory}
        sidebar={
          <>
            {loading ? (
              <div className="manage-product-categories-loading">{t('productCategories.loading')}</div>
            ) : categories.length === 0 ? (
              <div className="manage-product-categories-empty">{t('productCategories.empty')}</div>
            ) : (
              <div className="management-modal-sidebar-scroll manage-product-categories-list">
                {categories.map((category) => (
                <button
                  key={category.id}
                    type="button"
                    className={[
                      selectedCategoryId === category.id ? 'active' : '',
                      category.isActive ? '' : 'inactive',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  onClick={() => selectCategory(category)}
                >
                  <strong>{category.name}</strong>
                  <small>
                    {(category.internalId ?? t('productCategories.noInternalId'))}
                    {' | '}
                    {category.isActive ? t('productCategories.active') : t('productCategories.inactive')}
                  </small>
                </button>
              ))}
              </div>
            )}
          </>
        }
        form={
          <div className="manage-product-categories-form">
            <div className="management-modal-form-header">
              <h4>{draft.id ? t('productCategories.editCategory') : t('productCategories.createCategory')}</h4>
              <label className="management-modal-toggle manage-product-categories-active">
                <span>{t('productCategories.active')}</span>
                <input
                  type="checkbox"
                  checked={draft.isActive}
                  onChange={(event) => setDraft((current) => ({ ...current, isActive: event.target.checked }))}
                />
              </label>
            </div>

            <div className="manage-product-categories-mode">
              <div className={`manage-product-categories-mode-badge ${isEditingExistingCategory ? 'is-editing' : 'is-creating'}`}>
                {isEditingExistingCategory ? t('productCategories.editingModeTitle') : t('productCategories.creatingModeTitle')}
              </div>
              {isEditingExistingCategory ? (
                <button type="button" className="manage-product-categories-inline-action" onClick={startCreateCategory}>
                  {t('productCategories.switchToCreate')}
                </button>
              ) : null}
            </div>

            {error && <div className="manage-product-categories-error">{error}</div>}

            <div className="manage-product-categories-identity-row">
              <div className="manage-product-categories-field manage-product-categories-field--internal-id">
                <label htmlFor={categoryInternalIdInputId}>{t('productCategories.internalId')}</label>
                <input
                  id={categoryInternalIdInputId}
                  type="text"
                  value={draft.internalId}
                  onChange={(event) =>
                    setDraft((current) => ({
                      ...current,
                      internalId: event.target.value.toLowerCase().replace(/\s+/g, '_'),
                  }))
                }
                placeholder={t('productCategories.internalIdPlaceholder')}
              />
              <div className="manage-product-categories-field-hint">{t('productCategories.internalIdHint')}</div>
            </div>
            <div className="manage-product-categories-field manage-product-categories-field--name">
              <label htmlFor={categoryNameInputId}>{t('productCategories.name')}</label>
              <input
                id={categoryNameInputId}
                  ref={categoryNameInputRef}
                  type="text"
                  value={draft.name}
                onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
              />
            </div>
          </div>
          {isEditingExistingCategory ? (
            <div className="manage-product-categories-field-hint">{t('productCategories.editingHint')}</div>
          ) : null}
            <div className="manage-product-categories-actions">
              <button type="button" className="secondary" onClick={onClose}>
                {t('productCategories.close')}
              </button>
              {draft.id != null ? (
                <button
                  type="button"
                  className="danger"
                  onClick={() => setConfirmingDelete(true)}
                  disabled={saving || deleting}
                >
                  {deleting ? t('productCategories.deleting') : t('productCategories.deleteButton')}
                </button>
              ) : null}
              <button type="button" className="primary" onClick={handleSave} disabled={saving || deleting}>
                {saving
                  ? t('productCategories.saving')
                  : draft.id != null
                    ? t('productCategories.saveChanges')
                    : t('productCategories.createButton')}
              </button>
            </div>
          </div>
        }
      />
      {confirmingDelete && draft.id != null ? (
        <ConfirmActionModal
          title={t('confirm.deleteTitle')}
          message={t('confirm.deleteCategoryBody', { name: draft.name })}
          confirmLabel={deleting ? t('productCategories.deleting') : t('confirm.delete')}
          cancelLabel={t('confirm.cancel')}
          onConfirm={() => void handleDelete()}
          onCancel={() => {
            if (!deleting) {
              setConfirmingDelete(false);
            }
          }}
          loading={deleting}
        />
      ) : null}
    </>
  );
};

export default ManageProductCategoriesModal;
